import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, UserPlus } from "lucide-react";

import TeamFormModal from "../components/TeamFormModal";
import TeamCard from "../components/TeamCard";
import UpgradeToProModal from "../components/billing/UpgradeToProModal";

import * as teamService from "../services/teamService";
import useUpgradeToPro from "../hooks/useUpgradeToPro";
import { useCompany } from "../context/CompanyContext";

/* ===================================================== */
/* TEAMS PAGE                                            */
/* ===================================================== */

export default function TeamsPage() {
  const navigate = useNavigate();
  const { activeCompany } = useCompany();

  const isFreePlan = activeCompany?.plan === "free";
  const upgrade = useUpgradeToPro(activeCompany);

  /* ===================================================== */
  /* STATE                                                 */
  /* ===================================================== */

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  /* ===================================================== */
  /* LOAD TEAMS                                            */
  /* ===================================================== */

  const loadTeams = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const data = await teamService.getTeams();
      setTeams(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeCompany?._id) {
      loadTeams();
    }
  }, [activeCompany?._id, loadTeams]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTeams();
    setRefreshing(false);
  };

  /* ===================================================== */
  /* ACTIONS                                               */
  /* ===================================================== */

  const handleOpen = (team) => {
    navigate(`/teams/${team._id}`);
  };

  const handleSaved = async (payload) => {
    setSaving(true);

    try {
      if (payload._id) {
        const updated = await teamService.updateTeam(payload._id, payload);

        setTeams((prev) =>
          prev.map((t) => (t._id === updated._id ? updated : t))
        );
      } else {
        const created = await teamService.createTeam(payload);

        setTeams((prev) => [created, ...prev]);
      }

      setModalOpen(false);
      setEditing(null);
    } catch (e) {
      alert(e.message || "Failed to save team");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (team) => {
    if (!window.confirm(`Delete "${team.name}"?`)) return;

    setDeletingId(team._id);

    try {
      await teamService.deleteTeam(team._id);

      setTeams((prev) =>
        prev.filter((t) => t._id !== team._id)
      );
    } catch (e) {
      alert(e.message || "Failed to delete team");
    } finally {
      setDeletingId(null);
    }
  };

  /* ===================================================== */
  /* HELPERS                                               */
  /* ===================================================== */

  const requirePro = (fn) => {
    if (isFreePlan) {
      upgrade.openModal();
      return;
    }
    fn();
  };

  /* ===================================================== */
  /* SEARCH                                                */
  /* ===================================================== */

  const filteredTeams = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return teams;

    return teams.filter((t) =>
      (t.name || "").toLowerCase().includes(q)
    );
  }, [teams, search]);

  /* ===================================================== */
  /* SPLIT TEAMS                                           */
  /* ===================================================== */

  const internalTeams = useMemo(
    () =>
      filteredTeams.filter(
        (t) => t.type === "internal" || t.type === "founders"
      ),
    [filteredTeams]
  );

  const contractorTeams = useMemo(
    () => filteredTeams.filter((t) => t.type === "contractor"),
    [filteredTeams]
  );

  /* ===================================================== */
  /* GUARDS                                                */
  /* ===================================================== */

  if (!activeCompany) return null;

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="mx-auto max-w-7xl p-6">

      {/* HEADER */}
      <div className="mb-6 flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Teams</h1>

        <div className="ml-auto flex items-center gap-2">

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teams…"
            className="w-56 rounded-lg border px-3 py-2 text-sm"
          />

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />
          </button>

          {/* ONLY internal teams are creatable */}
          <button
            onClick={() =>
              requirePro(() => {
                setEditing(null);
                setModalOpen(true);
              })
            }
            className="flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
          >
            <UserPlus className="w-4 h-4" />
            + New Team
          </button>

        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 flex justify-between">
          <span>{error}</span>
          <button onClick={loadTeams} className="underline text-sm">
            Retry
          </button>
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div className="rounded-xl border bg-white p-6 text-sm text-gray-500">
          Loading teams…
        </div>
      )}

      {!loading && (
        <>
          {/* ================= INTERNAL ================= */}
          <Section title="Internal Teams" empty={internalTeams.length === 0}>
            {internalTeams.map((team) => (
              <TeamCard
                key={team._id}
                team={team}
                onOpen={() => handleOpen(team)}
                onEdit={() =>
                  requirePro(() => {
                    setEditing(team);
                    setModalOpen(true);
                  })
                }
                onDelete={() =>
                  requirePro(() => handleDelete(team))
                }
                deleting={deletingId === team._id}
              />
            ))}
          </Section>

          {/* ================= CONTRACTOR ================= */}
          <Section
            title="Contractor Teams"
            empty={contractorTeams.length === 0}
          >
            {contractorTeams.map((team) => (
              <TeamCard
                key={team._id}
                team={team}
                onOpen={() => handleOpen(team)}
                /* 🚫 NO edit / delete */
              />
            ))}
          </Section>
        </>
      )}

      {/* MODAL */}
      <TeamFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
        saving={saving}
      />

      {/* UPGRADE */}
      <UpgradeToProModal
        open={upgrade.open}
        onClose={upgrade.closeModal}
        onConfirm={upgrade.startUpgrade}
        status={upgrade.status}
        loading={upgrade.loading}
        error={upgrade.error}
      />
    </div>
  );
}

/* ===================================================== */
/* SECTION                                                */
/* ===================================================== */

function Section({ title, children, empty }) {
  return (
    <div className="mb-8">
      <h2 className="mb-3 text-lg font-semibold text-gray-900">
        {title}
      </h2>

      {empty ? (
        <div className="rounded-xl border bg-white p-6 text-sm text-gray-500">
          No teams found.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {children}
        </div>
      )}
    </div>
  );
}